import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, ShieldCheck } from 'lucide-react';
const values = [{
  icon: Building2,
  title: 'Commercial focus',
  description: 'We only work on commercial sites. That singular focus is why our installations last and our compliance knowledge runs deep.'
}, {
  icon: Users,
  title: 'In-house team',
  description: 'Every engineer is employed directly. No subcontractors, no agency staff. Trained on Daikin, Toshiba, and Mitsubishi systems.'
}, {
  icon: ShieldCheck,
  title: 'Long-term relationships',
  description: 'We maintain what we install. Many clients have been with us since year one. That is the best proof of quality we can offer.'
}];
export function About() {
  return <main className="min-h-screen font-sans text-primary-dark pt-28 bg-[#E8F0F8]" data-id="element-691">
      {/* Hero Section */}
      <section className="container mx-auto px-6 pb-20 pt-8" data-id="element-692">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start" data-id="element-693">
          <div className="lg:col-span-7" data-id="element-694">
            <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/40 mb-6 block" data-id="element-695">
              Our Story
            </span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-5xl md:text-6xl lg:text-7xl font-serif text-primary-dark leading-[1.1] tracking-tight" data-id="element-696">
              About AccuTemp
            </motion.h1>
          </div>
          <div className="lg:col-span-5 lg:pt-14" data-id="element-697">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-698">
              Commercial air conditioning specialists since 2010. Headquartered
              in Coventry, serving the whole of the UK.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="bg-white py-24" data-id="element-699">
        <div className="container mx-auto px-6" data-id="element-700">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-701">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-702">
              <h2 className="text-3xl md:text-4xl font-serif text-primary-dark mb-8" data-id="element-703">
                Built on commercial expertise
              </h2>
              <div className="space-y-6 text-primary-dark/70 leading-relaxed text-lg" data-id="element-704">
                <p data-id="element-705">
                  Founded over a decade ago, AccuTemp was built on a simple
                  premise: commercial buildings require a different level of
                  HVAC expertise than domestic properties.
                </p>
                <p data-id="element-706">
                  We saw too many businesses struggling with systems that were
                  improperly sized, poorly commissioned, or failing to meet
                  strict compliance standards like TM44 and F-Gas.
                </p>
                <p data-id="element-707">
                  By focusing exclusively on the commercial sector, we have
                  developed deep, specialised knowledge. From complex retail
                  refits to high-performance office environments, our in-house
                  team delivers solutions that last double the industry average.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative rounded-3xl overflow-hidden aspect-square md:aspect-[4/3] lg:aspect-square" data-id="element-708">
              <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80" alt="Modern commercial building" className="w-full h-full object-cover" data-id="element-709" />
              <div className="absolute inset-0 bg-primary-dark/10 mix-blend-multiply" data-id="element-710"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-711">
        <div className="container mx-auto px-6" data-id="element-712">
          <div className="grid md:grid-cols-3 gap-12" data-id="element-713">
            {values.map((value, index) => {
            const Icon = value.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} data-id="element-714">
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-accent shadow-sm mb-6" data-id="element-715">
                    <Icon className="w-7 h-7" data-id="element-716" />
                  </div>
                  <h3 className="text-2xl font-serif text-primary-dark mb-4" data-id="element-717">
                    {value.title}
                  </h3>
                  <p className="text-primary-dark/70 leading-relaxed" data-id="element-718">
                    {value.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Accreditations Bar */}
      <section className="bg-white py-16 border-t border-primary-dark/5" data-id="element-719">
        <div className="container mx-auto px-6" data-id="element-720">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-721">
            <div className="text-lg font-bold tracking-widest uppercase text-primary-dark" data-id="element-722">
              Constructionline Gold
            </div>
            <div className="text-lg font-bold tracking-widest uppercase text-primary-dark" data-id="element-723">
              Daikin D1+
            </div>
            <div className="text-lg font-bold tracking-widest uppercase text-primary-dark" data-id="element-724">
              REFCOM
            </div>
            <div className="text-lg font-bold tracking-widest uppercase text-primary-dark" data-id="element-725">
              SafeContractor
            </div>
            <div className="text-lg font-bold tracking-widest uppercase text-primary-dark" data-id="element-726">
              CHAS
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-dark py-24" data-id="element-727">
        <div className="container mx-auto px-6 text-center" data-id="element-728">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-729">
            <h2 className="text-3xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-730">
              Want to work with us?
            </h2>
            <p className="text-white/70 mb-10 text-lg leading-relaxed" data-id="element-731">
              Join the hundreds of businesses across the UK that trust AccuTemp
              with their commercial HVAC needs.
            </p>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-732">
              Get in touch
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-733" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}